import { z } from 'zod';

const resourceSchema = z.union([
  z.string().max(50),
  z.object({
    name: z.string().max(50),
    types: z.array(z.string().max(50)).max(20).optional(),
    idPrefixes: z.array(z.string().max(50)).max(50).optional(),
  }),
]);

const catalogExtraSchema = z.object({
  name: z.string().max(50),
  isRequired: z.boolean().optional(),
  options: z.array(z.string().max(100)).max(200).optional(),
});

export const catalogDefSchema = z.object({
  type: z.string().max(50),
  id: z.string().max(200),
  name: z.string().max(200).optional(),
  extra: z.array(catalogExtraSchema).max(20).optional(),
  extraSupported: z.array(z.string().max(50)).max(20).optional(),
  extraRequired: z.array(z.string().max(50)).max(20).optional(),
});

export const manifestSchema = z.object({
  id: z.string().min(1).max(200),
  version: z.string().min(1).max(50),
  name: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  logo: z.string().max(2000).optional(),
  background: z.string().max(2000).optional(),
  resources: z.array(resourceSchema).min(1).max(20),
  types: z.array(z.string().max(50)).max(20),
  idPrefixes: z.array(z.string().max(50)).max(50).optional(),
  catalogs: z.array(catalogDefSchema).max(100).default([]),
  behaviorHints: z
    .object({
      adult: z.boolean().optional(),
      p2p: z.boolean().optional(),
      configurable: z.boolean().optional(),
      configurationRequired: z.boolean().optional(),
    })
    .optional(),
});
export type AddonManifest = z.infer<typeof manifestSchema>;

export const SUPPORTED_RESOURCES = ['catalog', 'meta', 'stream', 'subtitles'] as const;
export type AddonResource = (typeof SUPPORTED_RESOURCES)[number];

export const installAddonSchema = z.object({
  manifestUrl: z.string().trim().max(2000),
});

export const updateAddonSchema = z.object({
  enabled: z.boolean().optional(),
  profileEnabled: z.boolean().optional(),
  priority: z.number().int().min(0).max(1000).optional(),
});

/** Every addon in the order the profile wants them, first to last. */
export const addonOrderSchema = z.object({
  ids: z.array(z.string().min(1).max(100)).min(1).max(200),
});

export interface AddonCapabilities {
  resources: AddonResource[];
  unsupportedResources: string[];
  types: string[];
  idPrefixes: string[];
  catalogs: { type: string; id: string; name: string }[];
  configurable: boolean;
  configurationRequired: boolean;
  p2p: boolean;
  adult: boolean;
}

export interface AddonPreview {
  manifest: {
    id: string;
    name: string;
    version: string;
    description: string | null;
    logo: string | null;
  };
  capabilities: AddonCapabilities;
  manifestUrl: string;
  host: string;
  warnings: string[];
  alreadyInstalled: boolean;
}

export interface AddonInstallation {
  id: string;
  addonId: string;
  name: string;
  version: string;
  description: string | null;
  logo: string | null;
  host: string;
  enabled: boolean;
  profileEnabled: boolean;
  priority: number;
  capabilities: AddonCapabilities;
  installedAt: string;
  lastSuccessAt: string | null;
  lastError: string | null;
  isDevFixture: boolean;
}
